import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ChevronUp, ChevronDown, MapPin, Radio, Thermometer, ExternalLink } from 'lucide-react';
import { getLocationImageUrl } from '../api';
import type { Location, LocationsFilters } from '../types';
import type { LocationColumnKey } from './ColumnVisibilityToggle';

interface LocationsTableProps {
  locations: Location[];
  sortKey?: LocationsFilters['sortKey'];
  sortDirection?: 1 | -1;
  onSort?: (key: LocationsFilters['sortKey']) => void;
  onEdit?: (location: Location) => void;
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  showAccount?: boolean;
  showSite?: boolean;
  visibleColumns?: LocationColumnKey[];
}

/**
 * Render tags as badges
 */
function TagsBadges({ tags }: { tags?: string[] }) {
  if (!tags || tags.length === 0) {
    return <span className="text-muted-foreground">-</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {tags.slice(0, 3).map((tag) => (
        <Badge key={tag} variant="secondary" className="text-xs">
          {tag}
        </Badge>
      ))}
      {tags.length > 3 && (
        <Badge variant="outline" className="text-xs">
          +{tags.length - 3}
        </Badge>
      )}
    </div>
  );
}

function SortIcon({ active, direction }: { active: boolean; direction: 1 | -1 }) {
  if (!active) {
    return <ChevronUp className="w-4 h-4 text-gray-300" />;
  }
  return direction === 1 ? (
    <ChevronUp className="w-4 h-4 text-gray-700" />
  ) : (
    <ChevronDown className="w-4 h-4 text-gray-700" />
  );
}

/**
 * Location image thumbnail with click-to-lightbox
 */
function LocationImage({
  location,
  onOpenLightbox,
}: {
  location: Location;
  onOpenLightbox: (url: string) => void;
}) {
  const orgId = typeof location.organisation === 'string' ? location.organisation : location.organisation?._id;
  const imageId = location.mongoImageId || (typeof location.image === 'string' ? location.image : undefined);
  const imageUrl = getLocationImageUrl(orgId, imageId);

  if (!imageUrl) {
    return (
      <div className="w-[60px] h-[60px] rounded bg-muted flex items-center justify-center">
        <span className="text-muted-foreground text-xs">-</span>
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={location.name}
      className="w-[60px] h-[60px] rounded object-cover cursor-pointer hover:opacity-80 transition-opacity"
      onClick={(e) => {
        e.stopPropagation();
        onOpenLightbox(imageUrl);
      }}
      onError={(e) => {
        (e.target as HTMLImageElement).style.display = 'none';
      }}
    />
  );
}

export function LocationsTable({
  locations,
  sortKey,
  sortDirection = 1,
  onSort,
  onEdit,
  page = 0,
  pageSize = 25,
  total = 0,
  onPageChange,
  showAccount = true,
  showSite = true,
  visibleColumns,
}: LocationsTableProps) {
  const { t } = useTranslation('locations');
  const totalPages = Math.ceil(total / pageSize) || 1;
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const isColVisible = (col: LocationColumnKey): boolean => {
    if (!visibleColumns) return true;
    return visibleColumns.includes(col);
  };

  const handleSort = (key: LocationsFilters['sortKey']) => {
    if (onSort) {
      onSort(key);
    }
  };

  const SortableHeader = ({
    sortKeyVal,
    children,
    className,
  }: {
    sortKeyVal: LocationsFilters['sortKey'];
    children: React.ReactNode;
    className?: string;
  }) => (
    <TableHead
      className={`cursor-pointer select-none hover:bg-gray-100 ${className || ''}`}
      onClick={() => handleSort(sortKeyVal)}
    >
      <div className="flex items-center gap-1">
        {children}
        <SortIcon active={sortKey === sortKeyVal} direction={sortDirection} />
      </div>
    </TableHead>
  );

  if (locations.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center text-muted-foreground">
        {t('table.noResults', 'No locations found matching your filters.')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              {isColVisible('image') && <TableHead className="w-[80px]">{t('table.image', 'Image')}</TableHead>}
              {isColVisible('name') && <SortableHeader sortKeyVal="name">{t('table.name', 'Name')}</SortableHeader>}
              {isColVisible('site') && showSite && (
                <SortableHeader sortKeyVal="siteName">{t('table.site', 'Site')}</SortableHeader>
              )}
              {isColVisible('account') && showAccount && (
                <SortableHeader sortKeyVal="accountName">
                  {t('table.account', 'Account')}
                </SortableHeader>
              )}
              {isColVisible('coordinates') && <SortableHeader sortKeyVal="coordinates">{t('table.coordinates', 'Coordinates')}</SortableHeader>}
              {isColVisible('transmitters') && <TableHead>{t('table.transmitters', 'Transmitters')}</TableHead>}
              {isColVisible('userGroup') && <TableHead>{t('table.userGroup', 'User Group')}</TableHead>}
              {isColVisible('sensors') && <TableHead>{t('table.sensors', 'Sensors')}</TableHead>}
              {isColVisible('tags') && <TableHead>{t('table.tags', 'Tags')}</TableHead>}
              {isColVisible('status') && <SortableHeader sortKeyVal="disabled">{t('table.status', 'Status')}</SortableHeader>}
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {locations.map((location) => {
              const transmitterCount = location.transmitterIds?.length || location.transmitters?.length || 0;
              const sensorCount = location.sensorIds?.length || location.sensors?.length || 0;
              const lat = location.latitude ?? location.coordinates?.[0];
              const lng = location.longitude ?? location.coordinates?.[1];

              return (
                <TableRow
                  key={location.id || location._id || location.mongoLocationId}
                  className="hover:bg-muted/50"
                >
                  {isColVisible('image') && (
                    <TableCell className="w-[80px] p-2">
                      <LocationImage
                        location={location}
                        onOpenLightbox={setLightboxUrl}
                      />
                    </TableCell>
                  )}
                  {isColVisible('name') && (
                    <TableCell>
                      <div className="font-medium">{location.name}</div>
                      {location.abbreviations && location.abbreviations.length > 0 && (
                        <div className="text-sm text-muted-foreground">
                          {location.abbreviations.join(', ')}
                        </div>
                      )}
                    </TableCell>
                  )}
                  {isColVisible('site') && showSite && (
                    <TableCell className="max-w-[180px] truncate">
                      {location.siteName || '-'}
                    </TableCell>
                  )}
                  {isColVisible('account') && showAccount && (
                    <TableCell className="max-w-[180px] truncate">
                      {location.accountName || '-'}
                    </TableCell>
                  )}
                  {isColVisible('coordinates') && (
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {lat != null && lng != null ? (
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 shrink-0" />
                          <span className="text-sm">{Number(lat).toFixed(4)}, {Number(lng).toFixed(4)}</span>
                        </div>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  )}
                  {isColVisible('transmitters') && (
                    <TableCell>
                      {transmitterCount > 0 ? (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Radio className="w-3 h-3 shrink-0 mt-0.5" />
                          {location.transmitterNames && location.transmitterNames.length > 0 ? (
                            <div className="max-h-[60px] overflow-y-auto text-sm">
                              {location.transmitterNames.map((name, i) => (
                                <div key={i} className="truncate max-w-[140px]">{name}</div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm">{transmitterCount}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  )}
                  {isColVisible('userGroup') && (
                    <TableCell className="text-sm text-muted-foreground">
                      {location.usergroupName || '-'}
                    </TableCell>
                  )}
                  {isColVisible('sensors') && (
                    <TableCell>
                      {sensorCount > 0 ? (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Thermometer className="w-3 h-3 shrink-0 mt-0.5" />
                          {location.sensorNames && location.sensorNames.length > 0 ? (
                            <div className="max-h-[60px] overflow-y-auto text-sm">
                              {location.sensorNames.map((name, i) => (
                                <div key={i} className="truncate max-w-[140px]">{name}</div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm">{sensorCount}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  )}
                  {isColVisible('tags') && (
                    <TableCell className="max-w-[200px]">
                      <TagsBadges tags={location.tags} />
                    </TableCell>
                  )}
                  {isColVisible('status') && (
                    <TableCell>
                      <Badge variant={location.disabled ? 'secondary' : 'default'}>
                        {location.disabled
                          ? t('status.inactive', 'Inactive')
                          : t('status.active', 'Active')}
                      </Badge>
                    </TableCell>
                  )}
                  <TableCell className="text-right">
                    {onEdit && (
                      <button
                        type="button"
                        onClick={() => onEdit(location)}
                        className="text-atek-blue hover:underline text-sm cursor-pointer"
                      >
                        Edit
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {t('pagination.pageOf', 'Page {{page}} of {{total}}', {
              page: page + 1,
              total: totalPages,
            })}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange?.(Math.max(0, page - 1))}
              disabled={page === 0}
            >
              {t('pagination.previous', 'Previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange?.(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
            >
              {t('pagination.next', 'Next')}
            </Button>
          </div>
        </div>
      )}

      {/* Image Lightbox */}
      <Dialog open={!!lightboxUrl} onOpenChange={(open) => { if (!open) setLightboxUrl(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('table.image', 'Image')}</DialogTitle>
          </DialogHeader>
          {lightboxUrl && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <img
                  src={lightboxUrl}
                  alt="Location"
                  className="max-h-[70vh] rounded object-contain"
                />
              </div>
              <div className="flex justify-end">
                <Button variant="outline" size="sm" asChild>
                  <a href={lightboxUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4 mr-1" />
                    {t('table.openNewTab', 'Open in new tab')}
                  </a>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
